/* oxlint-disable typescript/no-inferrable-types -- LINTER migration follow up */
import * as React from "react";

export const DEFAULT_DEBOUNCE_DELAY: number = 500;

type TInputValue = string | number;

interface DebounceValueParams<T extends HTMLInputElement | HTMLTextAreaElement> {
  initialValue: TInputValue;
  onChange: (event: React.ChangeEvent<T>) => void;
  onBlur?: (event: React.FocusEvent<T>) => void;
  delay?: number;
  /**
   * When initialValue changes, keep the in-progress input instead of resetting when it is semantically
   * equivalent (e.g. "1000," vs the echoed "1 000"). The pending commit is discarded either way.
   */
  shouldPreserveInputValue?: (currentValue: string, nextInitialValue: string) => boolean;
}

interface UseDebouncedValueResult<T extends HTMLInputElement | HTMLTextAreaElement> {
  value: string;
  handleChange: (event: React.ChangeEvent<T>) => void;
  handleBlur: (event: React.FocusEvent<T>) => void;
  setValue: (newValue: TInputValue, options?: { internalClearPending?: boolean }) => void;
  internalFlushPendingChange: () => void;
}

export function useDebouncedValue<T extends HTMLInputElement | HTMLTextAreaElement>({
  initialValue,
  onChange,
  onBlur,
  delay = DEFAULT_DEBOUNCE_DELAY,
  shouldPreserveInputValue,
}: DebounceValueParams<T>): UseDebouncedValueResult<T> {
  const normalizedInitialValue = React.useMemo(() => String(initialValue), [initialValue]);
  const [value, internalSetValue] = React.useState<string>(normalizedInitialValue);
  const [, startTransition] = React.useTransition();
  const timeoutIdRef = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const pendingChangeEventRef = React.useRef<React.ChangeEvent<T> | undefined>(undefined);
  const onChangeEffectEvent = React.useEffectEvent(onChange);
  // Effect event so the comparator and current value are read fresh without widening the sync effect's deps.
  const resolveSyncedValue = React.useEffectEvent((nextInitialValue: string): string =>
    shouldPreserveInputValue?.(value, nextInitialValue) ? value : nextInitialValue,
  );

  const clearPendingTimeout = React.useCallback(() => {
    clearTimeout(timeoutIdRef.current);
    timeoutIdRef.current = undefined;
  }, []);

  const internalFlushPendingChange = React.useCallback(() => {
    clearPendingTimeout();
    const pendingEvent = pendingChangeEventRef.current;
    if (!pendingEvent) {
      return;
    }
    pendingChangeEventRef.current = undefined;
    startTransition(() => {
      onChangeEffectEvent(pendingEvent);
    });
  }, [clearPendingTimeout]);

  const setValue = React.useCallback(
    (newValue: TInputValue, { internalClearPending = true }: { internalClearPending?: boolean } = {}) => {
      if (internalClearPending) {
        clearPendingTimeout();
        pendingChangeEventRef.current = undefined;
      }
      internalSetValue(String(newValue));
    },
    [clearPendingTimeout],
  );

  React.useLayoutEffect(() => {
    clearPendingTimeout();
    pendingChangeEventRef.current = undefined;
    // oxlint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect
    internalSetValue(resolveSyncedValue(normalizedInitialValue));
  }, [normalizedInitialValue, clearPendingTimeout]);

  const handleChange = React.useCallback(
    (e: React.ChangeEvent<T>) => {
      internalSetValue(e.target.value);
      pendingChangeEventRef.current = e;
      clearPendingTimeout();
      const timeoutId = setTimeout(() => {
        if (timeoutIdRef.current === timeoutId) {
          timeoutIdRef.current = undefined;
        }
        const pendingEvent = pendingChangeEventRef.current;
        if (!pendingEvent) {
          return;
        }
        pendingChangeEventRef.current = undefined;
        startTransition(() => {
          onChangeEffectEvent(pendingEvent);
        });
      }, delay);
      timeoutIdRef.current = timeoutId;
    },
    [delay, clearPendingTimeout],
  );

  React.useEffect(() => clearPendingTimeout, [clearPendingTimeout]);

  const handleBlur = React.useCallback(
    (e: React.FocusEvent<T>) => {
      clearPendingTimeout();
      if (pendingChangeEventRef.current !== undefined) {
        internalFlushPendingChange();
      }
      startTransition(() => {
        onBlur?.(e);
      });
    },
    [onBlur, clearPendingTimeout, internalFlushPendingChange],
  );

  return { value, handleChange, handleBlur, setValue, internalFlushPendingChange };
}
